/**
 * MergeProposalCard — the typed card for `propose_entry_merges`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Arc scanned the chronology for same-reality groups and classified each:
 * `coincide` (multiple accounts of one event → combine; every original
 * account is preserved on the merged card) or `contains` (a broad summary
 * covering specifics → the specifics survive, the summary absorbs into
 * them). The user applies each group individually through the build lane's
 * context → deterministic merge endpoint, which re-validates. Arc never
 * merges anything itself. Outside the build lane the card renders read-only.
 */
import React, { useState } from 'react';

import { useChronologyBuild } from './chronologyBuildContext';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  hint: { fontSize: 12, color: '#8a7fae', fontWeight: 400 },
  group: { borderTop: '1px solid #eee6fb', padding: '10px 12px' },
  relation: { fontSize: 11, padding: '1px 7px', borderRadius: 10, fontWeight: 600 },
  coincide: { background: '#e8f0ff', color: '#1a4fc0' },
  contains: { background: '#fdf2cf', color: '#7a5b00' },
  entry: { margin: '4px 0 0 12px', padding: '4px 8px', borderLeft: '2px solid #d6dae3', fontSize: 12.5 },
  meta: { fontSize: 12, color: '#667' },
  rationale: { fontSize: 12, color: '#667', fontStyle: 'italic', marginTop: 4 },
  merged: { marginTop: 6, padding: '6px 8px', borderRadius: 6, background: '#fff', border: '1px solid #e2d9f7' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 },
  btnPrimary: { padding: '5px 12px', borderRadius: 6, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  applied: { fontSize: 12, color: '#2c6e49', fontWeight: 600 },
  err: { color: '#c0392b', fontSize: 12 },
};

function EntryLine({ e, tag }) {
  return (
    <div style={S.entry}>
      <strong>{e.title || '(untitled)'}</strong>
      {tag && <span style={{ ...S.relation, ...S.contains, marginLeft: 6 }}>{tag}</span>}
      <span style={S.meta}> · {e.effective_date ? String(e.effective_date).slice(0, 10) : 'undated'}
        {e.review_state === 'suggested' ? ' · suggested' : ''}</span>
      {e.description && <div style={S.meta}>{e.description}</div>}
    </div>
  );
}

export default function MergeProposalCard({ result }) {
  const build = useChronologyBuild();
  const [appliedKeys, setAppliedKeys] = useState(() => new Set());
  const [error, setError] = useState('');
  const groups = result?.groups || [];

  if (!groups.length) {
    return (
      <div style={S.card}>
        <div style={S.head}>✨ No merge candidates</div>
        <div style={{ padding: '8px 12px', color: '#778' }}>
          {result?.dropped
            ? `${result.dropped} proposed group${result.dropped === 1 ? '' : 's'} failed validation.`
            : 'Nothing in this chronology looks like the same event told twice.'}
        </div>
      </div>
    );
  }

  const canApply = !!build?.mergeGroup && build.chronologyId === result.chronology_id;

  // contains groups: 'nest' (default — keep the summary as an episode,
  // destroys nothing) or 'absorb' (fold it into the specifics' lineage).
  const apply = async (g, key, mode) => {
    if (!canApply || build.busy || appliedKeys.has(key)) return;
    setError('');
    try {
      if (g.relation === 'contains' && mode !== 'absorb') {
        await build.nestGroup(g.summary_entry_id, g.child_entry_ids, key);
      } else if (g.relation === 'contains') {
        await build.mergeGroup({
          relation: 'contains',
          summary_entry_id: g.summary_entry_id,
          child_entry_ids: g.child_entry_ids,
        }, key);
      } else {
        await build.mergeGroup({
          relation: 'coincide',
          entry_ids: g.entry_ids,
          target_entry_id: g.target_entry_id,
          merged: { title: g.merged_title, description: g.merged_description },
        }, key);
      }
      setAppliedKeys((s) => new Set(s).add(key));
    } catch (e) {
      setError(e.message || 'Apply failed — the entries may have changed since this proposal.');
    }
  };

  return (
    <div style={S.card}>
      <div style={S.head}>
        <span>✨ Proposed merges</span>
        <span style={S.hint}>each is undoable; original accounts are preserved</span>
        {result.dropped > 0 && <span style={S.hint}>({result.dropped} invalid dropped)</span>}
      </div>
      {groups.map((g, i) => {
        const key = `${result.chronology_id}:${i}:${(g.entry_ids || [g.summary_entry_id]).join(',')}`;
        const applied = appliedKeys.has(key) || !!build?.mergedGroups?.[key];
        return (
          <div key={key} style={S.group}>
            <span style={{ ...S.relation, ...(g.relation === 'contains' ? S.contains : S.coincide) }}>
              {g.relation === 'contains' ? 'summary + specifics' : 'same event, multiple accounts'}
            </span>
            {g.relation === 'contains' ? (
              <>
                <EntryLine e={g.summary} tag="summary — will be absorbed" />
                {(g.entries || []).map((e) => <EntryLine key={e.entry_id} e={e} />)}
              </>
            ) : (
              <>
                {(g.entries || []).map((e) => (
                  <EntryLine key={e.entry_id} e={e} tag={e.entry_id === g.target_entry_id ? 'kept' : undefined} />
                ))}
                {(g.merged_title || g.merged_description) && (
                  <div style={S.merged}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#8a7fae' }}>COMBINED CARD</div>
                    {g.merged_title && <strong>{g.merged_title}</strong>}
                    {g.merged_description && <div>{g.merged_description}</div>}
                  </div>
                )}
              </>
            )}
            {g.rationale && <div style={S.rationale}>{g.rationale}</div>}
            <div style={S.actions}>
              {applied ? (
                <span style={S.applied}>✓ Applied</span>
              ) : g.relation === 'contains' ? (
                <>
                  <button
                    style={{ ...S.btnPrimary, ...(!canApply || build?.busy ? { opacity: 0.55, cursor: 'default' } : null) }}
                    onClick={() => apply(g, key, 'nest')}
                    disabled={!canApply || build?.busy}
                    title={canApply
                      ? 'Keep the summary as an episode — the specifics nest under it (Un-nest to undo)'
                      : 'Open this chronology’s Build lane to apply'}
                  >Make episode</button>
                  <button
                    style={{ ...S.btn, ...(!canApply || build?.busy ? { opacity: 0.55, cursor: 'default' } : null) }}
                    onClick={() => apply(g, key, 'absorb')}
                    disabled={!canApply || build?.busy}
                    title="Remove the summary — its text and sources fold into the specifics (Unmerge to undo)"
                  >Absorb instead</button>
                </>
              ) : (
                <button
                  style={{ ...S.btnPrimary, ...(!canApply || build?.busy ? { opacity: 0.55, cursor: 'default' } : null) }}
                  onClick={() => apply(g, key)}
                  disabled={!canApply || build?.busy}
                  title={canApply ? 'Merge these entries (undoable)' : 'Open this chronology’s Build lane to apply'}
                >Merge</button>
              )}
            </div>
          </div>
        );
      })}
      {error && <div style={{ ...S.err, padding: '0 12px 8px' }}>{error}</div>}
    </div>
  );
}
